import React, { useState, useEffect, useCallback } from 'react';
import { Box, useTheme, useMediaQuery } from '@mui/material';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import FloatingBottomNav from '../navigation/FloatingBottomNav';
import FloatingSidebar from '../navigation/FloatingSidebar';
import SharedListIntroBottomSheet from '../dialogs/SharedListIntroBottomSheet';
import AddStoryBottomSheet from '../dialogs/AddStoryBottomSheet';
import CreateBottomSheet, { type CreateChoice } from '../dialogs/CreateBottomSheet';
import CreateListDialog from '../dialogs/CreateListDialog';
import ImportDrawer, { type ImportFeedbackData } from '../dialogs/ImportDrawer';
import { importAnalyticsService } from '../../services/importAnalyticsService';
import ImportFeedbackDialog from '../dialogs/ImportFeedbackDialog';
import InstantWatchlistService from '../../services/instantWatchlistService';
import { ProcessedMovie } from '../../services/screenshotProcessingService';
import { useToast } from '../utils/ToastProvider';
import ThisOrThatGameDialog from '../widgets/ThisOrThatGameDialog';
import MovieTasteStepV2 from '../onboarding/MovieTasteStepV2';
import { useQuickTakes } from '../../hooks/useQuickTakes';
import { isFirebaseInitialized } from '@watchlist/shared';
import { MovieQuickViewProvider } from '../../contexts/MovieQuickViewContext';
import MovieQuickViewSheet from '../dialogs/MovieQuickViewSheet';
import { getCachedUserProfile } from '../../utils/profileCache';
import { watchlistService } from '../../services/watchlistService';
import { wasOpenedViaDeepLink, clearDeepLinkOpenedFlag } from '../../services/deepLinkService';


interface AppLayoutProps {
  children: React.ReactNode;
}

const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const userRef = React.useRef(user);
  useEffect(() => { userRef.current = user; }, [user]);

  const [showSharedIntro, setShowSharedIntro] = useState(false);
  const [navValue, setNavValue] = useState<number>(-1); // -1 means no tab selected
  const [sharedUserInfo, setSharedUserInfo] = useState<any>(null);
  const [sharedWatchlistInfo, setSharedWatchlistInfo] = useState<any>(null);
  const [, setIsSearchFocused] = useState(false);
  const [createSheetOpen, setCreateSheetOpen] = useState(false);
  const [addStoryOpen, setAddStoryOpen] = useState(false);
  const [listDialogMode, setListDialogMode] = useState<'solo' | 'collaborative' | null>(null);
  const [importDrawerOpen, setImportDrawerOpen] = useState(false);
  const [importFeedback, setImportFeedback] = useState<ImportFeedbackData | null>(null);
  const [recommendationFeedbackOpen, setRecommendationFeedbackOpen] = useState(false);
  const { showSuccess, showError } = useToast();

  const [thisOrThatOpen, setThisOrThatOpen] = useState(false);
  const quickTakes = useQuickTakes();

  // HomeCardStack + FeaturedSection CTAs (window events) — keep the click handlers
  // on the cards decoupled from the dialog state which lives here.
  useEffect(() => {
    const onCreate = () => setCreateSheetOpen(true);
    const onStory = () => setAddStoryOpen(true);
    const onThisOrThat = () => setThisOrThatOpen(true);
    const onInstantWatchlist = () => navigate('/watchlist/instant-watchlist');
    const onQuickTakes = () => quickTakes.open();
    const onRecommendationFeedback = () => setRecommendationFeedbackOpen(true);
    const onImportFeedback = (e: Event) => {
      try {
        const raw = localStorage.getItem('import_feedback_pending');
        if (raw) {
          setImportFeedback(JSON.parse(raw));
          return;
        }
        // Fallback: start a fresh analytics session so feedback can still be submitted
        const source = (e as CustomEvent<{ source: string }>).detail?.source as 'screenshot' | 'text' | 'link' | undefined;
        const uid = userRef.current?.uid;
        const sessionId = uid ? importAnalyticsService.startImportSession(uid, source ?? 'screenshot') : '';
        setImportFeedback({ sessionId, importSource: source ?? 'screenshot', importResults: { moviesDetected: 0, moviesSuccessful: 0, processingTime: 0 } });
      } catch { /* noop */ }
    };
    window.addEventListener('home-card:create', onCreate);
    window.addEventListener('home-card:story', onStory);
    window.addEventListener('featured:thisorthat', onThisOrThat);
    window.addEventListener('featured:instant-watchlist', onInstantWatchlist);
    window.addEventListener('featured:quicktakes', onQuickTakes);
    window.addEventListener('import:open-feedback', onImportFeedback);
    window.addEventListener('watchnow:open-feedback', onRecommendationFeedback);
    return () => {
      window.removeEventListener('home-card:create', onCreate);
      window.removeEventListener('home-card:story', onStory);
      window.removeEventListener('featured:thisorthat', onThisOrThat);
      window.removeEventListener('featured:instant-watchlist', onInstantWatchlist);
      window.removeEventListener('featured:quicktakes', onQuickTakes);
      window.removeEventListener('import:open-feedback', onImportFeedback);
      window.removeEventListener('watchnow:open-feedback', onRecommendationFeedback);
    };
  }, [navigate, quickTakes]);

  const handleCreateChoice = useCallback((choice: CreateChoice) => {
    setCreateSheetOpen(false);
    if (choice === 'add-movie') {
      // Delay navigation until the bottom sheet dismiss animation completes to avoid flicker.
      // Then dispatch search:focus after SearchPage has mounted and registered its listener.
      setTimeout(() => {
        navigate('/search');
        setTimeout(() => window.dispatchEvent(new Event('search:focus')), 100);
      }, 250);
    } else if (choice === 'story') setTimeout(() => setAddStoryOpen(true), 50);
    else if (choice === 'list-solo') setTimeout(() => setListDialogMode('solo'), 50);
    else if (choice === 'list-collab') setTimeout(() => setListDialogMode('collaborative'), 50);
    else if (choice === 'import') setTimeout(() => setImportDrawerOpen(true), 50);
  }, [navigate]);

  const handleMoviesDetected = useCallback(
    (detectedMovies: ProcessedMovie[], source: 'screenshot' | 'text' | 'link', sourceInfo?: string) => {
      try {
        switch (source) {
          case 'screenshot':
            InstantWatchlistService.saveScreenshotWatchlist(detectedMovies);
            showSuccess(`Detected ${detectedMovies.length} movies from screenshot!`);
            break;
          case 'text':
            InstantWatchlistService.saveTextWatchlist(detectedMovies);
            showSuccess(`Extracted ${detectedMovies.length} movies from text!`);
            break;
          case 'link':
            InstantWatchlistService.saveLinkWatchlist(detectedMovies, sourceInfo || 'Online list');
            showSuccess(`Imported ${detectedMovies.length} movies from ${sourceInfo}!`);
            break;
        }
        setImportDrawerOpen(false);
        navigate('/watchlist/instant-watchlist');
      } catch (error) {
        console.error(`Error creating ${source} watchlist:`, error);
        showError(`Failed to create watchlist from ${source}`);
      }
    },
    [navigate, showSuccess, showError]
  );

  // Get current tab value based on current route
  const getCurrentTab = useCallback(() => {
    const pathname = location.pathname;

    // Home is now the Crew feed
    if (pathname === '/') {
      return 0; // Home tab
    } else if (pathname === '/trending') {
      return 1; // Trending tab
    } else if (pathname.startsWith('/search')) {
      return 2; // Search tab
    } else if (pathname === '/profile') {
      return 4; // Profile tab
    }

    // /notifications and other pages have no nav tab selected (-1)
    return -1; // No tab selected (watchlist details, movie details, settings, etc.)
  }, [location.pathname]);

  // Check if this is a shared link on mount
  useEffect(() => {
    const isSharedLink = searchParams.get('shared') === 'true';
    const isFirstVisit = !localStorage.getItem('shared-intro-dismissed');
    const sharedByUid = searchParams.get('sharedBy');

    // SharedListIntroBottomSheet is watchlist-only (uses ?shared=true on /watchlist/*). Movie/review entry uses /open/movie/* or /review/* without requiring shared=.
    const eligibleForSharedWatchlistIntro =
      isSharedLink && isFirstVisit && location.pathname.startsWith('/watchlist/');

    if (eligibleForSharedWatchlistIntro) {
      setShowSharedIntro(true);
      setNavValue(-1); // No tab selected initially

      // Load shared user information if available
      if (sharedByUid) {
        // Check if Firebase is initialized before attempting to load user profile
        if (!isFirebaseInitialized()) {
          console.warn('AppLayout: Firebase not yet initialized, skipping shared user profile load');
          // Set fallback user info
          setSharedUserInfo({
            name: 'Unknown User',
            username: 'user',
            avatar: undefined,
            uid: sharedByUid,
          });
          return;
        }

        getCachedUserProfile(sharedByUid).then(userProfile => {
          if (userProfile) {
            console.log('AppLayout: Loaded shared user profile:', {
              displayName: userProfile.displayName,
              username: userProfile.username,
              hasUsername: !!userProfile.username,
              uid: userProfile.uid
            });

            setSharedUserInfo({
              name: userProfile.displayName || 'Unknown User',
              username: userProfile.username || userProfile.displayName?.toLowerCase().replace(/\s+/g, '') || 'user',
              avatar: userProfile.photoURL,
              uid: userProfile.uid,
            });
          } else {
            console.warn('AppLayout: No user profile found for shared user:', sharedByUid);
            // Set fallback user info
            setSharedUserInfo({
              name: 'Unknown User',
              username: 'user',
              avatar: undefined,
              uid: sharedByUid,
            });
          }
        }).catch(error => {
          console.error('Error loading shared user info:', error);
          // Set fallback user info on error
          setSharedUserInfo({
            name: `User ${sharedByUid.slice(-4)}`, // More user-friendly fallback
            username: `user${sharedByUid.slice(-4)}`,
            avatar: undefined,
            uid: sharedByUid,
          });
        });
      }

      // Load watchlist information dynamically from the current URL
      const pathParts = location.pathname.split('/');
      if (pathParts[1] === 'watchlist' && pathParts[2]) {
        const watchlistId = pathParts[2];
        const ownerId = searchParams.get('ownerId');
        const userId = searchParams.get('userId');
        // CRITICAL FIX: Prioritize ownerId (actual owner) for proper watchlist loading
        const actualOwnerId = userId || ownerId || sharedByUid; // Use userId first, then ownerId, then fallback to sharedBy

        if (actualOwnerId) {
          // Load watchlist data from the actual owner's watchlists
          console.log('AppLayout: Loading watchlist from owner:', actualOwnerId, '(shared by:', sharedByUid, ')');
          watchlistService.getUserWatchlists(actualOwnerId).then(userWatchlists => {
            const sharedWatchlist = userWatchlists.find(w => w.id === watchlistId);

            if (sharedWatchlist) {
              console.log('AppLayout: Loaded shared watchlist info:', {
                name: sharedWatchlist.name,
                movieCount: sharedWatchlist.movieDetails?.length || 0,
                isPrivate: sharedWatchlist.isPrivate,
                owner: actualOwnerId,
                sharedBy: sharedByUid
              });

              setSharedWatchlistInfo({
                name: sharedWatchlist.name,
                description: sharedWatchlist.description,
                movieCount: sharedWatchlist.movieDetails?.length || 0,
              });

              console.log('AppLayout: Successfully loaded shared watchlist info:', {
                name: sharedWatchlist.name,
                description: sharedWatchlist.description,
                movieCount: sharedWatchlist.movieDetails?.length || 0,
                watchlistId: watchlistId
              });
            } else {
              console.warn('AppLayout: Shared watchlist not found:', watchlistId);
              // Set fallback watchlist info
              setSharedWatchlistInfo({
                name: 'Shared Watchlist',
                description: undefined,
                movieCount: undefined,
              });
            }
          }).catch(error => {
            console.error('Error loading shared watchlist info:', error);
            // Set fallback watchlist info on error
            setSharedWatchlistInfo({
              name: 'Shared Watchlist',
              description: undefined,
              movieCount: undefined,
            });
          });
        } else {
          console.warn('AppLayout: No actualOwnerId found for shared watchlist');
        }
      }
    } else {
      setShowSharedIntro(false);
      // Normal tab detection when not showing shared watchlist intro
      const currentTab = getCurrentTab();
      console.log('AppLayout: Setting navValue to:', currentTab, 'for path:', location.pathname);
      setNavValue(currentTab);
    }
  }, [location.pathname, searchParams, getCurrentTab]);

  // Monitor search input focus state
  useEffect(() => {
    const checkSearchFocus = () => {
      const searchInput = document.querySelector('input[placeholder*="search" i], input[aria-label*="search" i]') as HTMLInputElement;
      setIsSearchFocused(!!searchInput && document.activeElement === searchInput);
    };

    // Check immediately
    checkSearchFocus();

    // Add event listeners for focus changes
    const handleFocusIn = () => checkSearchFocus();
    const handleFocusOut = () => setTimeout(checkSearchFocus, 100); // Small delay to handle rapid focus changes

    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);

    return () => {
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
    };
  }, [location.pathname]);

  // Determine if navigation should be visible
  const shouldShowNavigation = () => {
    const pathname = location.pathname;

    // ❌ ONLY HIDE navigation for onboarding flows
    if (pathname.startsWith('/onboarding')) {
      return false;
    }

    // ✅ SHOW navigation on ALL other pages
    return true;
  };

  // Check if we're on a top-level page (for mobile bottom nav only)
  const isTopLevelPage = () => {
    const pathname = location.pathname;
    return pathname === '/' ||
      pathname === '/trending' ||
      pathname === '/notifications' ||
      pathname === '/search' ||
      pathname === '/profile';
  };

  // Re-tapping the active tab scrolls it to the top. With keep-alive
  // TabContainers, each tab has its own scrollable element — find the active
  // one and scroll that. Falls back to window when no tab container is active.
  const handleScrollToTop = () => {
    const active = document.querySelector<HTMLElement>('[data-tab-container="active"]');
    if (active) {
      active.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Handle navigation changes
  const handleNavChange = (_event: React.SyntheticEvent, newValue: number) => {
    const currentTab = getCurrentTab();

    // Tapping the already-active tab always scrolls to top (no navigation).
    // Special case: re-tapping Search while on /search flips it into Active
    // mode (focused input + tabs) via a custom event SearchPage listens for.
    if (newValue === currentTab) {
      handleScrollToTop();
      if (newValue === 2 && location.pathname.startsWith('/search')) {
        window.dispatchEvent(new Event('search:activate'));
      } else {
        const searchInput = document.querySelector('input[placeholder*="search" i], input[aria-label*="search" i]') as HTMLInputElement;
        if (searchInput) searchInput.blur();
      }
      setNavValue(newValue);
      return;
    }

    // If the app was opened via a deep/shared link, replace the deep-link
    // history entry rather than pushing on top of it. Otherwise swiping back
    // from a tab would return to the shared item (which the user has already
    // dismissed by tapping a tab). Also clear the flag so subsequent back
    // navigation in the new tab behaves normally.
    const fromDeepLink = wasOpenedViaDeepLink();
    if (fromDeepLink) clearDeepLinkOpenedFlag();

    // Navigate to the selected tab
    switch (newValue) {
      case 0: navigate('/', { replace: fromDeepLink }); break;
      case 1: navigate('/trending', { replace: fromDeepLink }); break;
      case 2: navigate('/search', { replace: fromDeepLink }); break;
      case 4: navigate('/profile', { replace: fromDeepLink }); break;
    }

    setNavValue(newValue);
  };

  // Handle showing the watchlist after intro dismissal
  const handleShowWatchlist = () => {
    // Mark intro as dismissed
    localStorage.setItem('shared-intro-dismissed', 'true');
    setShowSharedIntro(false);
    // Keep navigation value at -1 (no tab selected) as requested
    setNavValue(-1);

    // Force a page refresh to ensure the WatchlistDetailRoute loads properly
    console.log('AppLayout: Dismissing intro and showing watchlist');

    // Small delay to ensure state updates complete
    setTimeout(() => {
      window.location.reload();
    }, 100);
  };

  // Handle intro close
  const handleCloseIntro = () => {
    localStorage.setItem('shared-intro-dismissed', 'true');
    setShowSharedIntro(false);
    setNavValue(-1); // No tab selected after dismissing
  };

  // Helper functions for user initials
  const getUserInitials = (displayName?: string | null) => {
    if (!displayName) return 'U';
    return displayName
      .split(' ')
      .map(name => name.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getInitialsBackgroundColor = (displayName?: string | null) => {
    if (!displayName) return '#5856D6';

    const colors = [
      '#5856D6', '#FF3B30', '#FF9500', '#FFCC02',
      '#34C759', '#00C7BE', '#007AFF', '#5AC8FA',
      '#AF52DE', '#FF2D92', '#A2845E'
    ];

    let hash = 0;
    for (let i = 0; i < displayName.length; i++) {
      hash = displayName.charCodeAt(i) + ((hash << 5) - hash);
    }

    return colors[Math.abs(hash) % colors.length];
  };

  return (
    <MovieQuickViewProvider>
    <Box
      sx={{
        minHeight: '100vh',
        position: 'relative',
        // Tab pages have their own bottom clearance inside TabContainer.
        // Non-tab pages don't show the bottom nav, so no outer padding needed.
      }}
    >
      {children}

      {/* Only show navigation if user is logged in, intro is not open, and should be visible */}
      {user && !showSharedIntro && shouldShowNavigation() && (
        <>
          {/* Mobile Bottom Navigation - ONLY on top-level pages */}
          {isMobile && isTopLevelPage() && (
            <FloatingBottomNav
              value={navValue}
              onChange={handleNavChange}
              user={user}
              getUserInitials={getUserInitials}
              getInitialsBackgroundColor={getInitialsBackgroundColor}
              onAddPress={() => setCreateSheetOpen(true)}
            />
          )}

          {/* Desktop Sidebar Navigation - ALWAYS visible on tablet/desktop */}
          {!isMobile && (
            <FloatingSidebar
              value={navValue}
              onChange={handleNavChange}
              user={user}
              getUserInitials={getUserInitials}
              getInitialsBackgroundColor={getInitialsBackgroundColor}
              onAddPress={() => setCreateSheetOpen(true)}
            />
          )}
        </>
      )}

      {/* Shared Link Intro Bottom Sheet */}
      <SharedListIntroBottomSheet
        open={showSharedIntro}
        onClose={handleCloseIntro}
        onShowWatchlist={handleShowWatchlist}
        sharedBy={sharedUserInfo}
        watchlistInfo={sharedWatchlistInfo}
      />

      {/* "+" → CreateBottomSheet dispatches to Story / List / Collaborative list */}
      <CreateBottomSheet
        open={createSheetOpen}
        onClose={() => setCreateSheetOpen(false)}
        onChoose={handleCreateChoice}
      />

      <AddStoryBottomSheet
        open={addStoryOpen}
        onClose={() => setAddStoryOpen(false)}
      />

      {listDialogMode !== null && user && (
        <CreateListDialog
          mode={listDialogMode}
          user={user}
          onBack={() => setListDialogMode(null)}
          onListCreated={(listId) => {
            setListDialogMode(null);
            navigate(`/watchlist/${listId}`);
          }}
        />
      )}

      <ImportDrawer
        open={importDrawerOpen}
        onClose={() => setImportDrawerOpen(false)}
        onMoviesDetected={handleMoviesDetected}
        onImportComplete={(data) => {
          setImportFeedback(data);
          try { localStorage.setItem('import_feedback_pending', JSON.stringify(data)); } catch { /* noop */ }
        }}
      />

      <ImportFeedbackDialog
        open={!!importFeedback}
        onClose={() => setImportFeedback(null)}
        sessionId={importFeedback?.sessionId}
        importSource={importFeedback?.importSource ?? 'screenshot'}
        importResults={importFeedback?.importResults}
        variant="import"
      />

      <ImportFeedbackDialog
        open={recommendationFeedbackOpen}
        onClose={() => setRecommendationFeedbackOpen(false)}
        importSource="recommendation"
        variant="recommendation"
      />

      {thisOrThatOpen && (
        <ThisOrThatGameDialog
          open={thisOrThatOpen}
          onClose={() => setThisOrThatOpen(false)}
          onWinnerDecided={() => { /* winner persistence handled inside the dialog */ }}
          userPreferredLanguages={quickTakes.preferredLanguages}
        />
      )}

      {quickTakes.isOpen && user && (
        <MovieTasteStepV2 {...quickTakes.dialogProps} />
      )}

      {/* Global movie quick-view sheet — keeps originating list mounted */}
      <MovieQuickViewSheet />
    </Box>
    </MovieQuickViewProvider>
  );
};

export default AppLayout; 